/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { StateToken, State, StateContext, Selector, Action } from '@ngxs/store';
import { patch, insertItem, updateItem, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminWorkAndRestRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { IWorkPlanTemplate } from '../../models/work-plan-template.model';
import { WorkPlanTemplateApiService } from '../../services/work-plan-template-api.service';
import {
  LoadWorkPlanTemplates,
  LoadWorkPlanTemplateById,
  CreateWorkPlanTemplate,
  UpdateWorkPlanTemplate,
  DeleteWorkPlanTemplate,
  LoadAllWorkPlanTemplates,
  ClearCurrentWorkPlanTemplate,
} from './work-plan-template.actions';

export interface WorkPlanStateModel {
  workPlansLoaded: boolean;
  workPlans: IWorkPlanTemplate[];
  allWorkPlans: IdName[];
  currentWorkPlan: IWorkPlanTemplate | undefined;
  workPlansTotalPages: number;
}

const WORK_PLAN_STATE_TOKEN = new StateToken<WorkPlanStateModel>('workPlan');

@State<WorkPlanStateModel>({
  name: WORK_PLAN_STATE_TOKEN,
  defaults: {
    workPlansLoaded: false,
    workPlans: [],
    allWorkPlans: [],
    workPlansTotalPages: 0,
    currentWorkPlan: undefined,
  },
})
@Injectable()
export class WorkPlanTemplateState {
  constructor(private service: WorkPlanTemplateApiService) {}

  @Action(LoadWorkPlanTemplates)
  loadWorkPlans(
    { getState, setState }: StateContext<WorkPlanStateModel>,
    { filter }: LoadWorkPlanTemplates
  ) {
    return this.service.loadWorkPlanTemplates(filter).pipe(
      tap((response: ApiResponse<IWorkPlanTemplate[]>) => {
        const state = getState();
        setState({
          ...state,
          workPlansLoaded: true,
          workPlans: response.data,
          workPlansTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllWorkPlanTemplates)
  loadAllWorkPlans({ getState, setState }: StateContext<WorkPlanStateModel>) {
    return this.service.loadAllWorkPlans().pipe(
      tap((response: IdName[]) => {
        const state = getState();
        setState({
          ...state,
          allWorkPlans: response,
        });
      })
    );
  }

  @Action(LoadWorkPlanTemplateById, { cancelUncompleted: true })
  loadWorkPlanById(
    { setState }: StateContext<WorkPlanStateModel>,
    { id }: LoadWorkPlanTemplateById
  ) {
    return this.service.loadWorkPlanById(id).pipe(
      tap((workPlan: IWorkPlanTemplate) => {
        setState(
          patch({
            currentWorkPlan: workPlan,
          })
        );
      })
    );
  }

  @Action(CreateWorkPlanTemplate)
  @OnSuccess({ url: AdminWorkAndRestRoutes.WorkPlanTemplates, message: 'workPlanCreated' })
  createWorkPlan(
    { setState }: StateContext<WorkPlanStateModel>,
    { workPlan }: CreateWorkPlanTemplate
  ) {
    return this.service.createWorkPlan(workPlan).pipe(
      tap((response: IWorkPlanTemplate) => {
        setState(
          patch({
            workPlans: insertItem<IWorkPlanTemplate>(response),
          })
        );
      })
    );
  }

  @Action(UpdateWorkPlanTemplate)
  @OnSuccess({ url: AdminWorkAndRestRoutes.WorkPlanTemplates, message: 'workPlanUpdated' })
  updateWorkPlan(
    { setState }: StateContext<WorkPlanStateModel>,
    { workPlan }: UpdateWorkPlanTemplate
  ) {
    return this.service.updateWorkPlan(workPlan).pipe(
      tap(() => {
        setState(
          patch({
            workPlans: updateItem<IWorkPlanTemplate>(
              (toUpdate) => toUpdate?.id === workPlan.id,
              workPlan
            ),
          })
        );
      })
    );
  }

  @Action(DeleteWorkPlanTemplate, { cancelUncompleted: true })
  @OnSuccess({ url: AdminWorkAndRestRoutes.WorkPlanTemplates, message: 'workPlanDeleted' })
  deleteWorkPlan(ctx: StateContext<WorkPlanStateModel>, { id }: DeleteWorkPlanTemplate) {
    return this.service.deleteWorkPlan(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            workPlans: removeItem<IWorkPlanTemplate>(
              (jtToDelete: IWorkPlanTemplate | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentWorkPlanTemplate)
  clearCurrentWorkPlan(ctx: StateContext<WorkPlanStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentWorkPlan: undefined,
    });
  }

  @Selector()
  static workPlansTotalPages(state: WorkPlanStateModel) {
    return state.workPlansTotalPages;
  }

  @Selector()
  static workPlans(state: WorkPlanStateModel): IWorkPlanTemplate[] {
    return state.workPlans;
  }

  @Selector()
  static allWorkPlans(state: WorkPlanStateModel): IdName[] {
    return state.allWorkPlans;
  }

  @Selector()
  static getCurrentWorkPlan(state: WorkPlanStateModel) {
    return state.currentWorkPlan;
  }
}
